import {KeyFrame} from 'common/src/modules/movie/moviePrimitives.types';
import {AnimationFunction} from 'common/src/modules/movieEditor/movieEditor.enums';
import {animationFunctions} from 'common/src/modules/movieEditor/movieEditor.consts';
import {AnimatedKeyframeInput} from '../agents/adBuilderAgent/adBuilderAgent.types';

export function buildKeyframedValue(
  keyframes: AnimatedKeyframeInput[],
  dim: number,
  options: {
    isOpacity?: boolean;
    isColor?: boolean;
    isScale?: boolean;
    isRotation?: boolean;
    propertyName?: string;
  },
): {
  a: number;
  ix: number;
  mult: number;
  sh: boolean;
  k?: KeyFrame<number[]>[];
  val?: number[];
} {
  if (keyframes.length === 0) {
    throw new Error(
      `At least one keyframe is required${options.propertyName ? ` for ${options.propertyName}` : ''}.`,
    );
  }

  let mult = 1;
  let defaultValue = 0;

  // Set multiplier and default value based on type
  if (options.isOpacity) {
    mult = 1 / 100; // Convert percentage to decimal
    defaultValue = 1;
  } else if (options.isRotation) {
    mult = Math.PI / 180; // Convert degrees to radians
    defaultValue = 0;
  } else if (options.isScale) {
    mult = 1 / 100; // Convert percentage to decimal
    defaultValue = 1;
  }

  // For single keyframe, create a simple non-keyframed value
  if (keyframes.length === 1) {
    const kf = keyframes[0];
    let value: number[] | undefined;

    // Handle all types based on dimension
    if (dim === 1) {
      value = [typeof kf.x === 'number' ? kf.x : defaultValue];
    } else if (dim === 2) {
      value = [
        typeof kf.x === 'number' ? kf.x : defaultValue,
        typeof kf.y === 'number' ? kf.y : defaultValue,
      ];
    } else if (dim === 3) {
      value = [
        typeof kf.x === 'number' ? kf.x : defaultValue,
        typeof kf.y === 'number' ? kf.y : defaultValue,
        typeof kf.z === 'number' ? kf.z : defaultValue,
      ];
    }

    return {
      val: value,
      a: 0,
      ix: 0,
      mult,
      sh: false,
    };
  }

  // Each keyframe: {x, y, z, time, easeIn, easeOut}
  const kfs: KeyFrame<number[]>[] = keyframes.map((kf) => {
    // Handle all types based on dimension
    let value: number[] | undefined;

    if (dim === 1) {
      value = [typeof kf.x === 'number' ? kf.x : defaultValue];
    } else if (dim === 2) {
      value = [
        typeof kf.x === 'number' ? kf.x : defaultValue,
        typeof kf.y === 'number' ? kf.y : defaultValue,
      ];
    } else if (dim === 3) {
      value = [
        typeof kf.x === 'number' ? kf.x : defaultValue,
        typeof kf.y === 'number' ? kf.y : defaultValue,
        typeof kf.z === 'number' ? kf.z : defaultValue,
      ];
    }

    return {
      t: kf.time,
      s: value,
    };
  });

  // Set e (end value) for each keyframe
  for (let i = 0; i < kfs.length; i++) {
    if (i < kfs.length - 1) {
      kfs[i].e = kfs[i + 1].s;
    } else {
      kfs[i].e = kfs[i].s;
    }
  }

  // ---important reminder about how keyframes works on After effects and our ad builder...do not delete this
  // Keyframe interpolation summary:
  // For each keyframe (of kfs):
  //   - keyframe.o is the interpolation output from this keyframe toward the next one.
  //   - keyframe.i is the interpolation input into the next one.
  //   - Both x/y values are between 0-1.
  //   - If keyframe.o should be linear, set x/y = 0.25, 0.25
  //   - If keyframe.i should be linear, set x/y = 0.75, 0.75
  //   - For keyframe.o ease in, set x/y = 0, 0.333
  //   - For keyframe.i ease in, set x/y = 1, 0.666
  // ---

  // Set i/o for each keyframe except the last
  for (let i = 0; i < kfs.length - 1; i++) {
    // Set o (out) based on this keyframe's easeOut
    let oType = AnimationFunction.Linear;
    if (keyframes[i].easeToNext) oType = AnimationFunction.EaseIn;
    const oX = Array(dim).fill(animationFunctions[oType].o?.x ?? 0);
    const oY = Array(dim).fill(animationFunctions[oType].o?.y ?? 0);
    kfs[i].o = {x: oX, y: oY};

    // Set i (in) based on the next keyframe's easeIn
    let iType = AnimationFunction.Linear;
    if (keyframes[i + 1]?.easeFromPrevious) iType = AnimationFunction.EaseOut;
    const iX = Array(dim).fill(animationFunctions[iType].i?.x ?? 0);
    const iY = Array(dim).fill(animationFunctions[iType].i?.y ?? 0);
    kfs[i].i = {x: iX, y: iY};
  }

  return {
    a: 0,
    ix: 0,
    mult,
    sh: false,
    k: kfs,
  };
}
